import { useTranslation } from 'react-i18next';
import { Button, Checkbox } from '@/components/ui';
import type { FacetGroup, FacetOption } from './facetTypes';

function FacetRow({
    group,
    option,
    onSelect,
}: {
    group: FacetGroup;
    option: FacetOption;
    onSelect: () => void;
}) {
    const { t } = useTranslation();
    const disabled = Boolean(option.disabledReason);
    return (
        <li key={option.key}>
            <Checkbox
                className={
                    'acc-rail__option' +
                    (option.selected ? ' is-active' : '') +
                    (disabled ? ' is-disabled' : '') +
                    (option.tone === 'warn' ? ' is-warn' : '')
                }
                checked={option.selected}
                disabled={disabled}
                onChange={onSelect}
                title={option.disabledReason}
                aria-label={t('console.accounts.facetRail.optionAriaLabel', {
                    group: group.label,
                    option: option.label,
                })}
            >
                <span className="acc-rail__label">{option.label}</span>
                <span className="acc-rail__count">
                    {option.count === null ? '—' : option.count.toLocaleString('vi-VN')}
                </span>
            </Checkbox>
        </li>
    );
}

export interface AccountsFacetRailProps {
    groups: FacetGroup[];
    onSelect: (groupKey: string, optionKey: string) => void;
    hasActiveFilters: boolean;
    onClear: () => void;
}

/**
 * EAA-1820: the ONE facet rail both `/console/accounts` (realm) and
 * `/console/hierarchy/accounts` (org) render — see `shared/facetTypes.ts` for why
 * a single `FacetGroup[]` shape could finally carry both. This file replaces the
 * realm's `accounts/AccountsFacetRail.tsx` (bespoke `facetCounts` object,
 * multi-select only) and the org's `hierarchy/scopedAccounts/ScopedAccountsFacets.tsx`
 * (single-select only, nullable counts, `disabledReason`/`tone`) — the union of
 * what both needed was already almost entirely the org shape, so this is that
 * shape rendered generically.
 *
 * The rail does not know or care whether a group is multi- or single-select:
 * every click calls `onSelect(groupKey, optionKey)` and the adapter (realm:
 * toggle-in-array; org: replace-the-one-value, per its exact-match query params)
 * decides what selecting it means. See each adapter's `selectFacet`.
 */
export function AccountsFacetRail({ groups, onSelect, hasActiveFilters, onClear }: AccountsFacetRailProps) {
    const { t } = useTranslation();
    return (
        <aside className="acc-rail" aria-label={t('console.accounts.facetRail.ariaLabel')}>
            {groups.map((group) => (
                <div key={group.key} className="acc-rail__group">
                    <h3 className="acc-rail__title">
                        {group.label}
                        {group.hint}
                    </h3>
                    <ul className="acc-rail__list">
                        {group.options.map((option) => (
                            <FacetRow
                                key={option.key}
                                group={group}
                                option={option}
                                onSelect={() => onSelect(group.key, option.key)}
                            />
                        ))}
                    </ul>
                    {group.note && <p className="acc-rail__note">{group.note}</p>}
                </div>
            ))}

            {hasActiveFilters && (
                <Button className="acc-rail__clear" onClick={onClear}>
                    {t('console.accounts.facetRail.clearFiltersButton')}
                </Button>
            )}
        </aside>
    );
}

export default AccountsFacetRail;
